import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { OperationStatus } from '../types';
import ApiError from '../types/error';
import {
  CategoryType,
  DeviceCategory,
  DeviceSubType,
  DeviceType
} from '../types/devices';
import { RootState } from '../store';

import {
  getDeviceCategoriesCleanup,
  getDeviceCategoriesRequest
} from '../features/devices/devices-slice';

// Map: categoryType -> deviceType -> [deviceSubType]
export type CategoryDeviceTypeMap = {
  [categoryType in CategoryType]?: {
    [deviceType in DeviceType]?: DeviceSubType[];
  };
};

// Map: deviceType -> [deviceSubType]
export type DeviceTypeMap = {
  [deviceType in DeviceType]?: DeviceSubType[];
};

export type UseDeviceCategoryMappingResult = {
  category: CategoryDeviceTypeMap;
  isLoading: boolean;
  error: ApiError | null;
};

/**
 * Hook to get device category mapping from API or state.
 */
const useDeviceCategoryMapping = (): UseDeviceCategoryMappingResult => {
  const dispatch = useDispatch();

  // Fetch categories on mount, cleanup on unmount
  useEffect(() => {
    dispatch(getDeviceCategoriesRequest());
    return () => dispatch(getDeviceCategoriesCleanup());
  }, [dispatch]);

  const {
    deviceCategories,
    getDeviceCategories: {
      status: getDeviceCategoriesStatus,
      error: getDeviceCategoriesError
    }
  } = useSelector((state: RootState) => state.DevicesReducer);

  return useMemo(() => {
    const category: CategoryDeviceTypeMap = {};
    if (
      getDeviceCategoriesStatus === OperationStatus.COMPLETED &&
      deviceCategories.length
    ) {
      deviceCategories.forEach((deviceCategory: DeviceCategory) => {
        const { category_type, device_type, device_sub_type } = deviceCategory;
        const categoryType = category_type as CategoryType;
        const deviceType = device_type as DeviceType;
        const deviceSubType = device_sub_type as DeviceSubType;
        if (!category[categoryType]) category[categoryType] = {};
        if (!category[categoryType]![deviceType]) {
          category[categoryType]![deviceType] = [];
        }
        if (
          deviceSubType &&
          !category[categoryType]![deviceType]!.includes(deviceSubType)
        ) {
          category[categoryType]![deviceType]!.push(deviceSubType);
        }
      });
    }
    return {
      category,
      isLoading: getDeviceCategoriesStatus === OperationStatus.RUNNING,
      error:
        getDeviceCategoriesStatus === OperationStatus.ERRORED
          ? (getDeviceCategoriesError as ApiError)
          : null
    };
  }, [getDeviceCategoriesStatus, deviceCategories, getDeviceCategoriesError]);
};

export default useDeviceCategoryMapping;
